import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { useDarkMode } from "../../../../context/ThemeContext";
import { getThemeClasses } from "../../../../utils/ThemeClasses";
import { useDebounce } from "../../../../hooks/useDebounce";
import PaginationControls from "../../../../components/base/PaginationControls";
import SgTable from "../../components/SgTable";
import type { SgColumn } from "../../components/SgTable";
import { fetchSgUsers, fetchImeisByUser } from "../../../../features/sg/usuarios";
import { useSgOrigins, originsOfImeis } from "../../../../features/sg/origins";
import { exportUsuarios, usuariosHeaders } from "../../../../features/sg/informes";
import { notifyOk, notifyError } from "../../../../features/sg/notify";
import {
  StatusBadge,
  OriginChips,
  UserCell,
  NumCell,
} from "../../components/cells";

const PER_PAGE = 30;

export default function UsersList() {
  const navigate = useNavigate();
  const { t } = useTranslation("sg");
  const { isDarkMode } = useDarkMode();
  const themeClasses = getThemeClasses(isDarkMode);

  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);
  const debounced = useDebounce(search, 400);

  async function handleExport() {
    setExporting(true);
    try {
      const n = await exportUsuarios(usuariosHeaders(t), debounced);
      notifyOk(t("users.exported", { n: n.toLocaleString("es-MX") }));
    } catch (err: any) {
      notifyError(err.message);
    } finally {
      setExporting(false);
    }
  }

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["sg-users", page, debounced],
    queryFn: () => fetchSgUsers(page, debounced, PER_PAGE),
    placeholderData: keepPreviousData,
  });

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const rows = data?.rows ?? [];

  // Origen calculado (unión de las plataformas de sus unidades) en lugar de
  // users.origin, que se elige a mano y sólo sirve para el vínculo con v5.
  const userIds = rows.map((u) => u.id);
  const { data: imeisByUser } = useQuery({
    queryKey: ["sg-user-imeis", userIds.join(",")],
    queryFn: () => fetchImeisByUser(userIds),
    enabled: userIds.length > 0,
    staleTime: 5 * 60_000,
  });
  const origins = useSgOrigins(Object.values(imeisByUser ?? {}).flat());

  const columns: SgColumn[] = [
    {
      header: t("users.cols.user"),
      key: "user_name",
      render: (u) => <UserCell name={u.user_name || "—"} isDarkMode={isDarkMode} />,
    },
    {
      header: t("barrido.cols.platforms"),
      key: "platforms",
      render: (u) => (
        <OriginChips origins={originsOfImeis(origins, imeisByUser?.[u.id] ?? [])} />
      ),
    },
    {
      header: t("users.cols.inactive"),
      key: "inactivos",
      align: "center",
      render: (u) => <span>{u.inactivos}</span>,
    },
    {
      header: t("users.cols.active"),
      key: "activos",
      align: "center",
      render: (u) => <span>{u.activos}</span>,
    },
    {
      header: t("users.cols.billable"),
      key: "facturables",
      align: "center",
      render: (u) => (
        <NumCell value={u.facturables} highlight isDarkMode={isDarkMode} />
      ),
    },
    {
      header: t("users.cols.total"),
      key: "total",
      align: "center",
      render: (u) => <span>{u.total}</span>,
    },
    {
      header: t("users.cols.businessName"),
      key: "business_name",
      render: (u) => <span>{u.business_name || "—"}</span>,
    },
    {
      header: t("users.cols.status"),
      key: "status_name",
      render: (u) => (
        <StatusBadge label={u.status_name || "—"} isDarkMode={isDarkMode} />
      ),
    },
  ];

  return (
    <div className="h-full min-h-0 w-full flex flex-col">
      <div
        className={`px-6 pt-6 pb-5 border-b ${
          isDarkMode ? "border-dark-border" : "border-gray-200"
        }`}
      >
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
              {t("users.title")}
            </h1>
            <p className={`text-sm mt-1 ${themeClasses.textSecondary}`}>
              {t("users.subtitle")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExport}
              disabled={exporting}
              className={`text-sm px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors whitespace-nowrap border ${
                isDarkMode
                  ? "border-dark-border text-gray-200 hover:bg-white/5"
                  : "border-gray-200 text-gray-700 hover:bg-gray-50"
              } disabled:opacity-60`}
            >
              <i className="ri-download-2-line"></i>
              <span>{exporting ? t("common.exporting") : t("common.export")}</span>
            </button>
            <button
              onClick={() => navigate("/sg/users/form")}
              className="bg-primary-500 hover:bg-primary-400 text-white text-sm px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line"></i>
              <span>{t("users.add")}</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1 max-w-md">
            <i
              className={`ri-search-line absolute left-3 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary} text-sm`}
            ></i>
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder={t("users.searchPlaceholder")}
              className={`w-full pl-10 pr-3 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                isDarkMode
                  ? "bg-dark-card border-dark-border text-white placeholder-gray-500"
                  : "bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400"
              }`}
            />
          </div>
          <span className={`text-sm ${themeClasses.textSecondary}`}>
            {t("users.count", { n: total.toLocaleString("es-MX") })}
          </span>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
        {isError ? (
          <p className="text-red-500 text-sm">
            {(error as Error)?.message || t("users.loadError")}
          </p>
        ) : (
          <>
            <SgTable
              columns={columns}
              rows={rows}
              isDarkMode={isDarkMode}
              loading={isPending}
              emptyText={t("users.empty")}
              rowKey={(u) => String(u.id)}
              onRowClick={(u) => navigate(`/sg/users/edit/${u.id}`)}
            />
            {totalPages > 1 && (
              <PaginationControls
                currentPage={page + 1}
                totalPages={totalPages}
                onPageChange={(p) => setPage(p - 1)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
